import type { ConfigSetting } from '@features/products/types/products.types';

export type ServiceCountMode = 'guided' | 'locked';

/**
 * The coherent min/max/default trio resolved from a product's config_settings.
 * Distinct from the endpoint's authoritative `DerivedScheduleBounds`: this is
 * the frontend's own read of the same catalog rows, used to decide the mode
 * and — for guided mode — how many authoritative entries to request
 * (`count = max`) before the endpoint response is available.
 */
export interface ServiceCountBounds {
    min: number;
    max: number;
    default: number;
}

const KEY_MIN = 'min_service_count';
const KEY_MAX = 'max_service_count';
const KEY_DEFAULT = 'default_service_count';
const ABSOLUTE_MAX = 10;
const DEFAULT_MIN = 2;
const DEFAULT_MAX = 10;
const DEFAULT_DEFAULT = 3;

/**
 * Mirrors smr-service's `ServiceCountBoundsResolver::unwrapInt` so the
 * frontend and backend agree on what counts as a clean integer count. Some
 * write paths wrap the scalar in a single-element array (mirroring the
 * money-JSON shape used elsewhere in this catalog); anything else — a
 * decimal, a float, a non-numeric string, an empty/multi-element array — is
 * rejected rather than coerced.
 */
function unwrapCount(value: unknown): number | null {
    let raw = value;

    if (Array.isArray(raw)) {
        if (raw.length !== 1) return null;
        raw = raw[0];
    }

    if (typeof raw === 'number') {
        return Number.isInteger(raw) ? raw : null;
    }

    if (typeof raw === 'string' && /^-?\d+$/.test(raw)) {
        return parseInt(raw, 10);
    }

    return null;
}

interface KeyRead {
    rows: number;
    value: number | null;
}

function readCount(settings: ConfigSetting[], key: string): KeyRead {
    const matches = settings.filter((setting) => setting.key === key);
    return {
        rows: matches.length,
        value: matches.length === 1 ? unwrapCount(matches[0].value) : null,
    };
}

function fill(read: KeyRead, fallback: number): number | null {
    if (read.rows === 0) return fallback;
    return read.value;
}

/** Effective bounds null or `min < max` -> guided; `min === max` -> locked. */
export function resolveServiceCountMode(
    configSettings?: ConfigSetting[] | null
): ServiceCountMode {
    const bounds = resolveEffectiveServiceCountBounds(configSettings);
    if (bounds && bounds.min === bounds.max) {
        return 'locked';
    }
    return 'guided';
}

/** Absent keys take the 2/10/3 defaults; typed keys are unaltered; malformed trios fail closed. */
export function resolveEffectiveServiceCountBounds(
    configSettings?: ConfigSetting[] | null
): ServiceCountBounds | null {
    const settings = configSettings ?? [];
    const min = fill(readCount(settings, KEY_MIN), DEFAULT_MIN);
    const max = fill(readCount(settings, KEY_MAX), DEFAULT_MAX);

    if (min === null || max === null) return null;

    const defaultRead = readCount(settings, KEY_DEFAULT);
    const defaultCount =
        defaultRead.rows === 0 ? Math.min(Math.max(DEFAULT_DEFAULT, min), max) : defaultRead.value;

    if (defaultCount === null) return null;

    const coherent =
        min >= 0 && min <= defaultCount && defaultCount <= max && max <= ABSOLUTE_MAX;

    return coherent ? { min, max, default: defaultCount } : null;
}
